import { useCallback, useEffect, useState } from 'react';
import { buildPlatformScope } from '@examflow/core';
import { api, type TrainingAttemptRecord, type TrainingAttemptRecordQuery } from '../../lib/api';
import type { TcfModuleId, UserLearningContext } from '@examflow/learning-space';

export type RecentTrainingAttemptsStatus = 'loading' | 'ready' | 'unavailable';

interface UseRecentTrainingAttemptsArgs extends TrainingAttemptRecordQuery {
  refreshKey?: number | string;
}

export function useRecentTrainingAttempts({
  userId,
  languageId,
  examType,
  skillId,
  contentPack,
  moduleId,
  intensity,
  source,
  reviewFocus,
  limit = 3,
  refreshKey,
}: UseRecentTrainingAttemptsArgs) {
  const [records, setRecords] = useState<TrainingAttemptRecord[]>([]);
  const [status, setStatus] = useState<RecentTrainingAttemptsStatus>('loading');

  const loadRecords = useCallback(async () => {
    if (!userId) {
      setRecords([]);
      setStatus('unavailable');
      return;
    }

    setStatus('loading');

    try {
      const { records: nextRecords } = await api.learning.getTrainingRecords({
        languageId,
        examType,
        skillId,
        contentPack,
        moduleId,
        intensity,
        source,
        reviewFocus,
        limit,
      });
      setRecords(nextRecords);
      setStatus('ready');
    } catch {
      setRecords([]);
      setStatus('unavailable');
    }
  }, [
    contentPack,
    examType,
    intensity,
    languageId,
    limit,
    moduleId,
    reviewFocus,
    skillId,
    source,
    userId,
  ]);

  useEffect(() => {
    void loadRecords();
  }, [loadRecords, refreshKey]);

  return {
    records,
    status,
    reload: loadRecords,
  };
}

export function buildLearningEvidenceRecordQueryScope(
  viewer: Pick<UserLearningContext, 'activeExamSpace'>,
  skillId?: TcfModuleId | null
): Pick<
  TrainingAttemptRecordQuery,
  'languageId' | 'examType' | 'skillId' | 'contentPack' | 'moduleId'
> {
  const platformScope = buildPlatformScope({
    examType: viewer.activeExamSpace.examCode,
    skillId: skillId ?? undefined,
  });

  return {
    languageId: platformScope?.languageId,
    examType: platformScope?.examType ?? viewer.activeExamSpace.examCode,
    skillId: skillId ?? undefined,
    contentPack: viewer.activeExamSpace.examCode,
    moduleId: skillId ?? undefined,
  };
}
